import { useEffect } from 'react';
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ssgSnapshot from './generated/ssg-snapshot.json';
import { useAuth } from './lib/auth';
import { captureAffiliateRef } from './lib/affiliateRef';
import { syncThemeAfterHydration } from './lib/theme';
import { ScrollManager } from './components/ScrollManager';
import { PublicAnalytics } from './components/PublicAnalytics';
import { ErrorBoundary as AppErrorBoundary } from './components/ErrorBoundary';
// `?inline` returns the processed CSS as a STRING instead of emitting a hashed asset + a
// render-blocking <link>. We render it as a <style> in <Layout> below so first paint no longer
// waits a network round-trip for /assets/root-*.css (Lighthouse "render-blocking requests",
// ~150 ms on Slow 4G). Because the <style> is part of React's tree it renders identically during
// prerender and hydration — unlike a post-build HTML rewrite, which desyncs React Router's
// <Links/> and throws hydration error #418. Trade-off: the CSS string also rides the root JS
// chunk (~13 KB Brotli), but that removes one request AND unblocks the critical path.
import rootCss from './index.css?inline';

/**
 * Root route module — replaces BOTH the old `index.html` shell and `RootLayout` in routes.tsx.
 *
 * In framework mode React Router owns the whole document, so the shell that used to live in
 * index.html (charset, viewport, theme-color, favicon, font preconnect + the print→all swap) moves
 * into `Layout` below. `<Meta />` and `<Links />` are where each route's `meta()` / `links()`
 * output lands — that is what replaces the react-helmet-async `<Seo>` component.
 */

const queryClient = new QueryClient();

// Seed the cache with public config prefetched at BUILD time (scripts/prefetch-public-data.mjs).
// Kept as-is for now so the migration is behaviour-preserving; it is scheduled for deletion once
// the public routes move to real `loader()`s, which run during prerender natively (step 4 of
// docs/migration-react-router-ssg.md).
for (const [key, data] of Object.entries(ssgSnapshot as Record<string, unknown>)) {
  queryClient.setQueryData(JSON.parse(key) as unknown[], data);
}

/**
 * Applies the stored theme to <html> before the first paint.
 *
 * Has to be an inline string, not an import: a module would be fetched and evaluated AFTER the
 * document paints, so light-mode visitors would see the dark theme flash first. The storage key
 * is duplicated from lib/theme.ts for the same reason — keep the two in step.
 *
 * This is only about PAINT. React's own idea of the theme still starts at SSR_THEME and is
 * corrected by syncThemeAfterHydration() below; see the comments in lib/theme.ts for why
 * reading localStorage during render is a hydration bug rather than a shortcut.
 */
const THEME_SCRIPT = `try{var t=localStorage.getItem('sieve.theme');if(t!=='light'&&t!=='dark')t='dark';var e=document.documentElement;e.dataset.theme=t;e.style.colorScheme=t}catch(_){}`;

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    // suppressHydrationWarning: THEME_SCRIPT below sets data-theme + style.colorScheme on this
    // element before React hydrates, so the live <html> legitimately carries attributes the
    // prerendered markup does not. Scoped to this element only — it does not extend to children.
    <html lang="en" suppressHydrationWarning>
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {/* Must stay FIRST and inline: it has to run before first paint, or a light-mode
            visitor gets a flash of the dark theme on every navigation. */}
        <script dangerouslySetInnerHTML={{ __html: THEME_SCRIPT }} />
        {/* Per-page SEO — title, description, robots, canonical, OpenGraph, Twitter and all
            JSON-LD — comes from each route's `meta()` export and lands in <Meta /> below. This
            shell keeps only theme colours, icons and fonts. */}
        <meta name="theme-color" content="#090a0e" media="(prefers-color-scheme: dark)" />
        <meta name="theme-color" content="#f3f5f9" media="(prefers-color-scheme: light)" />

        {/* SVG favicon renders crisply at every size; apple-touch-icon for iOS home-screen pins. */}
        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link rel="apple-touch-icon" href="/favicon.svg" />

        {/* Fonts are self-hosted under /fonts/ (see the @font-face blocks in index.css), so there
            is no cross-origin Google Fonts stylesheet to preconnect to, swap in, or guard against
            FOUC — the whole print→all dance is gone. We preload only the two ABOVE-THE-FOLD latin
            faces — Bricolage (headings) + Familjen (body) — with `crossorigin` so the preload
            matches the CORS-anonymous fetch the browser makes for @font-face files. JetBrains Mono
            is deliberately NOT preloaded: its @font-face is defined but only downloads on pages
            that actually render mono text (code snippets, /docs). latin-ext loads on demand too. */}
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
          href="/fonts/bricolage-grotesque-v9-latin.woff2"
        />
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          crossOrigin="anonymous"
          href="/fonts/familjen-grotesk-v11-latin.woff2"
        />

        <Meta />
        <Links />

        {/* Critical CSS inlined (see the `?inline` import up top) — paints without a round-trip
            for /assets/root-*.css. Rendered by React so prerender and hydration agree. Kept AFTER
            <Meta/>/<Links/> so the ~75 KB of styles don't push title/OG/JSON-LD deep into the
            document (OG scrapers that read only the first chunk would miss them). CSS in <head>
            applies regardless of position, so paint is unaffected. */}
        <style dangerouslySetInnerHTML={{ __html: rootCss }} />
      </head>
      <body>
        {/* #root is NOT vestigial from the old index.html — index.css styles it directly.
            `#root { position: relative; z-index: 1 }` is what lifts the page above the
            `body::before` grid + glow backdrop (position: fixed, z-index: 0). Render children
            straight into <body> and that selector matches nothing, so the backdrop paints OVER
            the content and washes the whole site in the glow gradients. It also shares the
            `html, body, #root` block that sets min-height/background/overflow-x. */}
        <div id="root">{children}</div>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  const init = useAuth((s) => s.init);
  useEffect(() => {
    // Runs after hydration, so correcting the theme here cannot desync React from the
    // prerendered markup the way reading localStorage during render did.
    syncThemeAfterHydration();
    captureAffiliateRef();
    void init();
  }, [init]);

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ScrollManager />
        <PublicAnalytics />
        <Outlet />
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
